import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/contexts/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import ScrollToTop from "@/components/ScrollToTop";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import Passport from "@/pages/Passport";
import Certificates from "@/pages/Certificates";
import VerifyPage from "@/pages/Verify";
import VerifyDetail from "@/pages/VerifyDetail";
import Pricing from "@/pages/Pricing";
import HRDashboard from "@/pages/HRDashboard";
import Legal from "@/pages/Legal";
import PrivacyCenter from "@/pages/PrivacyCenter";
import TrustSafety from "@/pages/TrustSafety";
import Students from "@/pages/Students";
import OrgProfile from "@/pages/OrgProfile";
import Billing from "@/pages/Billing";
import EmployeesPage from "@/pages/workspace/EmployeesPage";
import AddEmployeePage from "@/pages/workspace/AddEmployeePage";
import StudentsDirectoryPage from "@/pages/workspace/StudentsPage";
import AddStudentPage from "@/pages/workspace/AddStudentPage";
import BulkUploadPage from "@/pages/workspace/BulkUploadPage";
import MyRecordsPage from "@/pages/MyRecordsPage";
import EmployeeProfilePage from "@/pages/workspace/EmployeeProfilePage";
import IssueCertificatePage from "@/pages/workspace/IssueCertificatePage";
import ApprovalsInboxPage from "@/pages/workspace/ApprovalsInboxPage";
import ApprovalSettingsPage from "@/pages/workspace/ApprovalSettingsPage";
import HRStaffPage from "@/pages/workspace/HRStaffPage";
import MobileBottomNav from "@/components/MobileBottomNav";
import Institutions from "@/pages/Institutions";
import Employers from "@/pages/Employers";
import Sitemap from "@/pages/Sitemap";
import Developers from "@/pages/Developers";
import ConnectedAccounts from "@/pages/ConnectedAccounts";
import AdminPartners from "@/pages/admin/AdminPartners";
import GetRecognized from "@/pages/GetRecognized";
import DemoRequest from "@/pages/DemoRequest";
import AdminInquiries from "@/pages/admin/AdminInquiries";
import AdminAwards from "@/pages/admin/AdminAwards";
import VerifyInstitutionAward from "@/pages/VerifyInstitutionAward";
import InstitutionAwardsPublic from "@/pages/InstitutionAwardsPublic";
import MyAwardsPage from "@/pages/workspace/MyAwardsPage";
import AdminEntry from "@/pages/admin/AdminEntry";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminIssuers from "@/pages/admin/AdminIssuers";
import AdminCertificates from "@/pages/admin/AdminCertificates";
import AdminDisputes from "@/pages/admin/AdminDisputes";
import AdminAuditLogs from "@/pages/admin/AdminAuditLogs";
import AdminSecurity from "@/pages/admin/AdminSecurity";
import AdminMFA from "@/pages/admin/AdminMFA";
import TrustSafetyDashboard from "@/pages/admin/TrustSafetyDashboard";
import TrustSafetyAlerts from "@/pages/admin/TrustSafetyAlerts";
import TrustSafetyAccount from "@/pages/admin/TrustSafetyAccount";
import TrustSafetySettings from "@/pages/admin/TrustSafetySettings";
import AdminPlans from "@/pages/admin/AdminPlans";
import AdminCoupons from "@/pages/admin/AdminCoupons";
import AdminPayments from "@/pages/admin/AdminPayments";
import AdminApiPortal from "@/pages/admin/AdminApiPortal";
import AdminPlatform from "@/pages/admin/AdminPlatform";
import AdminComms from "@/pages/admin/AdminComms";
import AdminCMS from "@/pages/admin/AdminCMS";
import AdminStaff from "@/pages/admin/AdminStaff";
import AdminOps from "@/pages/admin/AdminOps";
import { ThemeProvider } from "@/contexts/ThemeContext";
import usePageTitle from "@/hooks/usePageTitle";
import "@/index.css";

function PageTitleSync() { usePageTitle(); return null; }

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
        <ScrollToTop />
        <PageTitleSync />
        <Toaster position="top-right" richColors />
        <MobileBottomNav />
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/institutions" element={<Institutions />} />
          <Route path="/employers" element={<Employers />} />
          <Route path="/for-employers" element={<Employers />} />
          <Route path="/sitemap" element={<Sitemap />} />
          <Route path="/developers" element={<Developers />} />
          <Route path="/connected-accounts" element={<ProtectedRoute><ConnectedAccounts /></ProtectedRoute>} />
          <Route path="/admin/partners" element={<ProtectedRoute roles={["super_admin"]}><AdminPartners /></ProtectedRoute>} />
          <Route path="/get-recognized" element={<GetRecognized />} />
          <Route path="/demo" element={<DemoRequest />} />
          <Route path="/verify" element={<VerifyPage />} />
          <Route path="/verify/institution/:awardNo" element={<VerifyInstitutionAward />} />
          <Route path="/institutions/:ethicId/awards" element={<InstitutionAwardsPublic />} />
          <Route path="/verify/:ethicId" element={<VerifyDetail />} />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/passport"
            element={
              <ProtectedRoute>
                <Passport />
              </ProtectedRoute>
            }
          />
          <Route
            path="/certificates"
            element={
              <ProtectedRoute>
                <Certificates />
              </ProtectedRoute>
            }
          />
          <Route
            path="/hr-dashboard"
            element={
              <ProtectedRoute roles={["organization", "institution", "super_admin"]}>
                <HRDashboard />
              </ProtectedRoute>
            }
          />
          <Route path="/passport/:ethicId" element={<VerifyDetail />} />
          <Route path="/legal" element={<Legal />} />
          <Route path="/legal/:slug" element={<Legal />} />
          <Route path="/privacy-center" element={<PrivacyCenter />} />
          <Route path="/trust-safety" element={<TrustSafety />} />
          <Route path="/for-students" element={<Students />} />
          <Route path="/students" element={<Students />} />
          <Route path="/org-profile" element={<ProtectedRoute roles={["organization", "institution", "verifier", "super_admin"]}><OrgProfile /></ProtectedRoute>} />
          <Route path="/billing" element={<ProtectedRoute><Billing /></ProtectedRoute>} />

          {/* Workspace — Employees (Org) */}
          <Route path="/workspace/employees" element={<ProtectedRoute roles={["organization", "super_admin"]}><EmployeesPage /></ProtectedRoute>} />
          <Route path="/workspace/employees/add" element={<ProtectedRoute roles={["organization", "super_admin"]}><AddEmployeePage /></ProtectedRoute>} />
          <Route path="/workspace/employees/bulk" element={<ProtectedRoute roles={["organization", "super_admin"]}><BulkUploadPage kind="employees" /></ProtectedRoute>} />
          <Route path="/workspace/employees/:id" element={<ProtectedRoute roles={["organization", "super_admin"]}><EmployeeProfilePage /></ProtectedRoute>} />
          <Route path="/workspace/certificates/issue" element={<ProtectedRoute roles={["organization", "institution", "super_admin"]}><IssueCertificatePage /></ProtectedRoute>} />
          <Route path="/workspace/approvals" element={<ProtectedRoute roles={["organization", "institution", "super_admin"]}><ApprovalsInboxPage /></ProtectedRoute>} />
          <Route path="/workspace/settings/approval" element={<ProtectedRoute roles={["organization", "institution", "super_admin"]}><ApprovalSettingsPage /></ProtectedRoute>} />
          <Route path="/workspace/staff" element={<ProtectedRoute roles={["organization", "institution", "super_admin"]}><HRStaffPage /></ProtectedRoute>} />
          <Route path="/workspace/my-awards" element={<ProtectedRoute roles={["institution", "super_admin"]}><MyAwardsPage /></ProtectedRoute>} />

          {/* Workspace — Students (Institution) */}
          <Route path="/workspace/students" element={<ProtectedRoute roles={["institution", "super_admin"]}><StudentsDirectoryPage /></ProtectedRoute>} />
          <Route path="/workspace/students/add" element={<ProtectedRoute roles={["institution", "super_admin"]}><AddStudentPage /></ProtectedRoute>} />
          <Route path="/workspace/students/bulk" element={<ProtectedRoute roles={["institution", "super_admin"]}><BulkUploadPage kind="students" /></ProtectedRoute>} />

          {/* Individual: My Records (claim suggested matches) */}
          <Route path="/my-records" element={<ProtectedRoute roles={["professional"]}><MyRecordsPage /></ProtectedRoute>} />

          {/* Super Admin Master Control — hidden entry */}
          <Route path="/adentry" element={<AdminEntry />} />
          <Route path="/admin" element={<ProtectedRoute roles={["super_admin"]}><AdminDashboard /></ProtectedRoute>} />
          <Route path="/admin/users" element={<ProtectedRoute roles={["super_admin"]}><AdminUsers /></ProtectedRoute>} />
          <Route path="/admin/organizations" element={<ProtectedRoute roles={["super_admin"]}><AdminIssuers kind="organizations" /></ProtectedRoute>} />
          <Route path="/admin/institutions" element={<ProtectedRoute roles={["super_admin"]}><AdminIssuers kind="institutions" /></ProtectedRoute>} />
          <Route path="/admin/certificates" element={<ProtectedRoute roles={["super_admin"]}><AdminCertificates /></ProtectedRoute>} />
          <Route path="/admin/disputes" element={<ProtectedRoute roles={["super_admin"]}><AdminDisputes /></ProtectedRoute>} />
          <Route path="/admin/audit-logs" element={<ProtectedRoute roles={["super_admin"]}><AdminAuditLogs /></ProtectedRoute>} />
          <Route path="/admin/security" element={<ProtectedRoute roles={["super_admin"]}><AdminSecurity /></ProtectedRoute>} />
          <Route path="/admin/mfa" element={<ProtectedRoute roles={["super_admin"]}><AdminMFA /></ProtectedRoute>} />
          <Route path="/admin/trust-safety" element={<ProtectedRoute roles={["super_admin"]}><TrustSafetyDashboard /></ProtectedRoute>} />
          <Route path="/admin/trust-safety/alerts" element={<ProtectedRoute roles={["super_admin"]}><TrustSafetyAlerts /></ProtectedRoute>} />
          <Route path="/admin/trust-safety/account/:uid" element={<ProtectedRoute roles={["super_admin"]}><TrustSafetyAccount /></ProtectedRoute>} />
          <Route path="/admin/trust-safety/settings" element={<ProtectedRoute roles={["super_admin"]}><TrustSafetySettings /></ProtectedRoute>} />
          <Route path="/admin/plans" element={<ProtectedRoute roles={["super_admin"]}><AdminPlans /></ProtectedRoute>} />
          <Route path="/admin/coupons" element={<ProtectedRoute roles={["super_admin"]}><AdminCoupons /></ProtectedRoute>} />
          <Route path="/admin/payments" element={<ProtectedRoute roles={["super_admin"]}><AdminPayments /></ProtectedRoute>} />
          <Route path="/admin/api-portal" element={<ProtectedRoute roles={["super_admin"]}><AdminApiPortal /></ProtectedRoute>} />
          <Route path="/admin/platform" element={<ProtectedRoute roles={["super_admin"]}><AdminPlatform /></ProtectedRoute>} />
          <Route path="/admin/communications" element={<ProtectedRoute roles={["super_admin"]}><AdminComms /></ProtectedRoute>} />
          <Route path="/admin/cms" element={<ProtectedRoute roles={["super_admin"]}><AdminCMS /></ProtectedRoute>} />
          <Route path="/admin/staff" element={<ProtectedRoute roles={["super_admin"]}><AdminStaff /></ProtectedRoute>} />
          <Route path="/admin/ops" element={<ProtectedRoute roles={["super_admin"]}><AdminOps /></ProtectedRoute>} />
          <Route path="/admin/inquiries" element={<ProtectedRoute roles={["super_admin"]}><AdminInquiries /></ProtectedRoute>} />
          <Route path="/admin/awards" element={<ProtectedRoute roles={["super_admin"]}><AdminAwards /></ProtectedRoute>} />
        </Routes>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
